import { Children, forwardRef, useCallback, useRef } from "react";
import { DialogTriggerContext } from "./DialogTriggerContext";
import { augmentElement, isNil, mergeProps, resolveChildren, useControllableState, useEventCallback } from "../../shared";
import { Overlay, useOverlayLightDismiss, useOverlayTrigger } from "../../overlay";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DefaultElement = "div";
export function InnerDialogTrigger({
  as = DefaultElement,
  children,
  defaultOpen,
  dismissable = true,
  forwardedRef,
  onOpenChange,
  open: openProp,
  zIndex = 1,
  ...rest
}) {
  const [isOpen, setIsOpen] = useControllableState(openProp, defaultOpen, false);
  const dialogRef = useRef();
  const updateIsOpen = useCallback((event, newValue) => {
    setIsOpen(newValue);

    if (!isNil(onOpenChange)) {
      onOpenChange(event, newValue);
    }
  }, [onOpenChange, setIsOpen]);
  const open = useCallback(event => {
    updateIsOpen(event, true);
  }, [updateIsOpen]);
  const close = useCallback(event => {
    updateIsOpen(event, false);
  }, [updateIsOpen]);
  const [trigger, dialog] = Children.toArray(resolveChildren(children, {
    close
  }));

  if (isNil(trigger) || isNil(dialog)) {
    throw new Error("A dialog trigger must have exactly 2 children.");
  }

  const triggerProps = useOverlayTrigger(isOpen, {
    hideOnLeave: false,
    onHide: useEventCallback(event => {
      close(event);
    }),
    onShow: useEventCallback(event => {
      open(event);
    })
  });
  const overlayDismissProps = useOverlayLightDismiss(dialogRef, {
    hideOnEscape: isOpen,
    hideOnLeave: false,
    hideOnOutsideClick: isOpen && dismissable,
    onHide: useEventCallback(event => {
      updateIsOpen(event, false);
    })
  });
  const triggerMarkup = augmentElement(trigger, triggerProps);
  const dialogMarkup = augmentElement(dialog, {
    dismissable,
    ref: dialogRef,
    zIndex: zIndex + 1
  });
  return /*#__PURE__*/_jsxs(DialogTriggerContext.Provider, {
    value: {
      close,
      isOpen
    },
    children: [triggerMarkup, /*#__PURE__*/_jsx(Overlay, { ...mergeProps(rest, {
        as,
        ref: forwardedRef,
        show: isOpen,
        zIndex
      }, overlayDismissProps),
      children: dialogMarkup
    })]
  });
}
InnerDialogTrigger.defaultElement = DefaultElement;
export const DialogTrigger = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerDialogTrigger, { ...props,
  forwardedRef: ref
}));